import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, MapPin, Users, FileCheck } from "lucide-react";
import { useLang } from "@/i18n";
import { Reveal } from "@/components/Reveal";

const BG = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=2000&q=80";

export default function Trust() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} data-testid="trust-section" className="relative overflow-hidden bg-[#0E1B2A] py-24 sm:py-32">
      <motion.div style={{ y }} className="absolute inset-0 -top-[15%] -bottom-[15%]">
        <img src={BG} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-15" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0E1B2A]/60 via-transparent to-[#0E1B2A]/70" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D4AF37]">{t.trust.overline}</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#FAF8F5]">{t.trust.title}</h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#FAF8F5]/65">{t.trust.sub}</p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <div
              data-testid="badge-interhome"
              className="flex items-center gap-3 rounded-full border border-[#D4AF37]/30 bg-white/5 px-7 py-4 backdrop-blur-sm transition-all duration-500 hover:border-[#D4AF37]/60 hover:bg-white/10"
            >
              <span className="font-display text-lg sm:text-xl font-semibold tracking-[0.15em] text-[#FAF8F5]">INTERHOME</span>
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">{t.trust.partnerLabel}</span>
            </div>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-3">
          {[
            { id: "badge-siret", icon: FileCheck, title: "SIRET 75396469100020", sub: t.trust.siretLabel },
            { id: "badge-location", icon: MapPin, title: t.trust.locationLabel, sub: "Nice · Côte d'Azur" },
            { id: "badge-team", icon: Users, title: t.trust.teamLabel, sub: "Service Plus · 2025" },
          ].map(({ id, icon: Icon, title, sub }, i) => (
            <Reveal key={id} delay={0.2 + i * 0.08}>
              <div
                data-testid={id}
                className="flex h-full items-start gap-4 rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm transition-all duration-500 hover:border-[#D4AF37]/40 hover:bg-white/10"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#D4AF37]/15 text-[#D4AF37]">
                  <Icon size={20} />
                </span>
                <div>
                  <p className="text-sm sm:text-base font-semibold text-[#FAF8F5]">{title}</p>
                  <p className="mt-1 text-xs uppercase tracking-widest text-[#FAF8F5]/50">{sub}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.3} className="mt-10 flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/10 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#E8D9A0]">
            <ShieldCheck size={14} />
            Service Plus · 2025
          </span>
        </Reveal>
      </div>
    </section>
  );
}
